import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CONTAINER_VIEW_SLOT_COUNT,
  REPORT_BULLET_FIELDS,
  REPORT_SECTIONS,
  REPORT_TABLE_FIELDS,
  REPORT_TEXT_FIELDS,
  createLimiter,
  generateContainerLoadingReport,
  stripHumidityUnit,
} from "../../../utils/americaReport/containerLoadingReport";
import { customerApi } from "../../../utils/apiConfig";

function getBackendAssetUrl(fileUrl) {
  if (!fileUrl) return "";

  if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
    return fileUrl;
  }

  const baseURL = customerApi?.defaults?.baseURL || "";

  const backendOrigin = baseURL.includes("/api/")
    ? baseURL.split("/api/")[0]
    : baseURL.replace(/\/+$/, "");

  return `${backendOrigin.replace(/\/+$/, "")}${
    fileUrl.startsWith("/") ? fileUrl : `/${fileUrl}`
  }`;
}

// Galeri kareleri 150px civarı; orijinal kamera fotoğrafını (onlarca MB'lık
// decode) her karede çözmek modalı kasıyordu. Küçük bir önizleme üretip onu
// gösteriyoruz: görünür olunca, aynı anda birkaç tane, sonuç önbellekte.
const THUMB_WIDTH = 320;
const thumbCache = new Map();
const thumbPromises = new Map();
const limitThumbWork = createLimiter(3);

function loadThumbnail(url) {
  if (thumbPromises.has(url)) return thumbPromises.get(url);

  const promise = limitThumbWork(async () => {
    const response = await fetch(url, { credentials: "include" });

    if (!response.ok) throw new Error("thumb");

    const blob = await response.blob();
    let bitmap;

    try {
      bitmap = await createImageBitmap(blob, {
        imageOrientation: "from-image",
        resizeWidth: THUMB_WIDTH,
        resizeQuality: "medium",
      });
    } catch (error) {
      bitmap = await createImageBitmap(blob);
    }

    const scale = Math.min(1, THUMB_WIDTH / bitmap.width);
    const canvas = document.createElement("canvas");

    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close && bitmap.close();

    const thumb = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.75)
    );

    if (!thumb) throw new Error("thumb");

    const objectUrl = URL.createObjectURL(thumb);

    thumbCache.set(url, objectUrl);

    return objectUrl;
  });

  promise.catch(() => thumbPromises.delete(url));
  thumbPromises.set(url, promise);

  return promise;
}

function PhotoThumb({ url, alt }) {
  const holderRef = useRef(null);
  const [src, setSrc] = useState(() => thumbCache.get(url) || "");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (src || failed) return undefined;

    const node = holderRef.current;
    let cancelled = false;

    const start = () =>
      loadThumbnail(url).then(
        (thumbUrl) => !cancelled && setSrc(thumbUrl),
        () => !cancelled && setFailed(true)
      );

    if (!node || typeof IntersectionObserver === "undefined") {
      start();
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          start();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [url, src, failed]);

  const imageStyle = {
    width: "100%",
    height: 120,
    objectFit: "cover",
    display: "block",
  };

  return (
    <div ref={holderRef} style={{ height: 120, background: "#f1f5f9" }}>
      {(src || failed) && (
        <img
          src={src || url}
          alt={alt}
          loading="lazy"
          decoding="async"
          style={imageStyle}
        />
      )}
    </div>
  );
}

// Bir veya birden fazla konteyner kaydının fotoğraflarını, seçim sırasına göre
// (1. konteynerin fotoğrafları, ardından 2.'nin, ...) tek bir havuzda birleştirir.
function buildPhotoPool(records) {
  const list = Array.isArray(records) ? records : [];

  return list.flatMap((record) => {
    const recordId = String(record?._id || record?.konteynerNo || "");
    const containerNo = String(record?.konteynerNo || "").trim() || "Konteyner";
    const photos = Array.isArray(record?.photos) ? record.photos : [];

    return photos
      .filter((photo) => photo?.fileUrl)
      .map((photo) => ({
        id: String(photo._id || photo.fileUrl),
        url: getBackendAssetUrl(photo.fileUrl),
        label:
          photo.cameraLabel ||
          photo.cameraKey ||
          photo.visualKey ||
          photo.photoType ||
          "Fotoğraf",
        recordId,
        containerNo,
        capturedAt: photo.capturedAt,
      }));
  });
}

const DEFAULT_TEXTS = {
  SUBJECT:
    "Pre-Galvanized Tube Cargoes at Goktas Factory, Gebze, Kocaeli/ TURKIYE",
  INTRO:
    "During the export loading process, container inspection and general suitability assessment of galvanised pipes were carried out by our company's quality department.",
  CARGO: "PRE-GALVANIZED TUBE",
  LOAD_PORT: "YILPORT, IZMIT",
  DISCHARGE_PORT: "OAKLAND- UNITED STATES",
  SHIPPER: "GOKTAS YASSI HADDE MAM. SAN.VE TIC. A.S.",
  CUSTOMER: "",
  QUANTITY: "",
  TOTAL_QUANTITY: "",
  C1_1: "Containers were physically found in sound condition.",
  C1_2:
    "No any harmful physical damage (hole / tear / major deformation, etc.) was observed on the containers’ steel and structure.",
  C1_3: "Containers’ were dry and suitable for the loading of the cargo.",
  C1_4:
    "Before the commencement of the loading operation, a light test, verification of whether the ventilation systems were open and humidity control were carried out.",
  C2_1: "All wooden dunnages were observed as dry and marked.",
  C2_2:
    "Wooden dunnages were covered with kraft paper also used under and between the bundles.",
  C2_3:
    "10 to 15 pieces of TR2300 daN capacity of polyester straps were applied on the cargoes stuffed inside each container for lashing.",
  C2_4:
    "Kraft papers were used on containers’ ground and also cargoes were covered with kraft paper.",
  C3_1:
    "Cargoes were lifting with crane equipped with canvas slings and stuffed inside the containers by special apparatus.",
  C3_2: "30 pieces of moisture bags were placed inside each container.",
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function ordinalSuffix(day) {
  if (day >= 11 && day <= 13) return "th";

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

export function formatEnglishDate(date = new Date()) {
  const day = date.getDate();

  return `${day}${ordinalSuffix(day)} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

function parseRecordDate(value) {
  if (!value) return new Date();

  // "16.09.2026 11:04:22" -> Date
  const match = String(value).match(/^(\d{2})\.(\d{2})\.(\d{4})/);

  if (match) {
    return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

// REPORT_TABLE_FIELDS key'lerini (HUMIDITY, BOOKING_NO, ...) containerRows
// state'indeki camelCase alan adlarına çevirir (humidity, bookingNo, ...).
const TABLE_FIELD_TO_ROW_FIELD = {
  HUMIDITY: "humidity",
  BOOKING_NO: "bookingNo",
  BUNDLES: "bundles",
  WEIGHT: "weight",
  REF_NO: "refNo",
};

function toRowField(tableFieldKey) {
  return TABLE_FIELD_TO_ROW_FIELD[tableFieldKey] || tableFieldKey;
}

function findObservation(form, key) {
  const rows = [
    ...(Array.isArray(form?.productControls) ? form.productControls : []),
    ...(Array.isArray(form?.loadingControls) ? form.loadingControls : []),
  ];

  const row = rows.find((item) => item?.key === key);

  return String(row?.observation || "").trim();
}

export function buildInitialValues({ records }) {
  const list = Array.isArray(records) ? records : [];
  const first = list[0] || {};
  const firstForm = first?.loadingControlForm || {};

  const reportDate = formatEnglishDate(
    parseRecordDate(firstForm?.formHeader?.tarih || first?.createdAt)
  );

  const containerCount = list.length || 1;

  return {
    ...DEFAULT_TEXTS,
    DATE: reportDate,
    CUSTOMER: String(
      firstForm?.formHeader?.musteri || first?.firmaAdi || ""
    ).trim(),
    QUANTITY: `${containerCount} CONTAINERS /   PACKAGES  /   KGS`,
    TOTAL_QUANTITY: `${containerCount} CONTAINERS /   PACKAGES /   KGS`,
    C3_2: DEFAULT_TEXTS.C3_2,
  };
}

// "B - Loaded Cargo Quantity" tablosunun konteyner başına bir satırı;
// Konteyner No kayıttan otomatik gelir, geri kalanı operatör doldurur.
export function buildInitialContainerRows({ records }) {
  const list = Array.isArray(records) ? records : [];

  return list.map((record) => ({
    recordId: String(record?._id || record?.konteynerNo || ""),
    containerNo: String(record?.konteynerNo || "").trim(),
    humidity: stripHumidityUnit(
      findObservation(record?.loadingControlForm, "ahsap_nem_olcer")
    ),
    bookingNo: "",
    bundles: "",
    weight: "",
    refNo: String(record?.loadingControlForm?.formHeader?.refNo || "").trim(),
  }));
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1300,
  padding: 16,
};


const dialogStyle = {
  background: "#fff",
  borderRadius: 16,
  width: "min(1100px, 100%)",
  maxHeight: "92vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)",
};

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid #d9d9d9",
  borderRadius: 8,
  fontSize: 14,
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  marginBottom: 4,
};

const primaryButtonStyle = {
  border: "none",
  borderRadius: 8,
  padding: "10px 18px",
  color: "#fff",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  background: "#1677ff",
};

const secondaryButtonStyle = {
  border: "1px solid #d9d9d9",
  borderRadius: 8,
  padding: "10px 18px",
  background: "#fff",
  color: "#334155",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
};

export default function AmericaReportModal({ open, onClose, records = [] }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState(() => buildInitialValues({ records }));
  const [containerRows, setContainerRows] = useState(() =>
    buildInitialContainerRows({ records })
  );
  // Konteyner Numarası adımı: recordId -> seçilen fotoğraf url'leri (sırayla).
  const [containerPhotos, setContainerPhotos] = useState({});
  const [selections, setSelections] = useState({});
  // Küçük kare önizlemede numara/etiket okunamıyor: tıklayınca büyük önizleme.
  const [zoomedPhoto, setZoomedPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    setStepIndex(0);
    setValues(buildInitialValues({ records }));
    setContainerRows(buildInitialContainerRows({ records }));
    setContainerPhotos({});
    setSelections({});
    setProgress("");
    setError("");
    // Üst bileşen her render'da yeni bir `records` dizisi veriyor; yalnızca
    // modal açılırken sıfırla, yoksa seçimler ve önizlemeler boşuna silinir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const photos = useMemo(() => buildPhotoPool(records), [records]);

  const steps = useMemo(
    () => [
      { key: "__form", label: "Rapor Bilgileri" },
      { key: "__containerView", label: "Konteyner Numarası" },
      ...REPORT_SECTIONS,
    ],
    []
  );

  const totalSelected = useMemo(() => {
    const containerCount = Object.values(containerPhotos).reduce(
      (sum, urls) => sum + (Array.isArray(urls) ? urls.length : 0),
      0
    );

    return (
      containerCount +
      REPORT_SECTIONS.reduce(
        (sum, section) => sum + (selections[section.key]?.length || 0),
        0
      )
    );
  }, [selections, containerPhotos]);

  const activeStepKey = steps[stepIndex].key;
  const activeStepLimit = steps[stepIndex].slots
    ? steps[stepIndex].slots.length
    : 0;

  // Kare bileşenleri memo'lu; tıklamada tüm galeri yeniden çizilmesin diye
  // bu işleyiciler sabit kalmalı.
  const handlePhotoToggle = useCallback(
    (url) => {
      setSelections((prev) => {
        const current = Array.isArray(prev[activeStepKey])
          ? prev[activeStepKey]
          : [];

        if (current.includes(url)) {
          return {
            ...prev,
            [activeStepKey]: current.filter((item) => item !== url),
          };
        }

        if (current.length >= activeStepLimit) return prev;

        return { ...prev, [activeStepKey]: [...current, url] };
      });
    },
    [activeStepKey, activeStepLimit]
  );

  const toggleContainerPhoto = useCallback((url, recordId) => {
    setContainerPhotos((prev) => {
      const current = Array.isArray(prev[recordId]) ? prev[recordId] : [];

      if (current.includes(url)) {
        return { ...prev, [recordId]: current.filter((item) => item !== url) };
      }

      if (current.length >= CONTAINER_VIEW_SLOT_COUNT) return prev;

      return { ...prev, [recordId]: [...current, url] };
    });
  }, []);

  if (!open) return null;

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const updateContainerRow = (recordId, field, value) => {
    setContainerRows((prev) =>
      prev.map((row) =>
        row.recordId === recordId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleGenerate = async () => {
    setBusy(true);
    setError("");

    try {
      const containers = containerRows.map((row) => ({
        containerNo: row.containerNo,
        humidity: row.humidity,
        bookingNo: row.bookingNo,
        bundles: row.bundles,
        weight: row.weight,
        refNo: row.refNo,
        photos: containerPhotos[row.recordId] || [],
      }));

      const safeContainer =
        containers
          .map((c) => c.containerNo)
          .filter(Boolean)
          .join("-")
          .replace(/[\\/:*?"<>|]/g, "-") || "container";

      await generateContainerLoadingReport({
        values,
        containers,
        selections,
        fileName: `CONTAINER LOADING REPORT ${safeContainer}.docx`,
        onProgress: setProgress,
      });

      onClose();
    } catch (generateError) {
      setError(generateError?.message || "Rapor oluşturulamadı.");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const activeStep = steps[stepIndex];
  const isFormStep = activeStep.key === "__form";
  const isContainerViewStep = activeStep.key === "__containerView";
  const isLastStep = stepIndex === steps.length - 1;

  const bulletGroups = REPORT_BULLET_FIELDS.reduce((acc, field) => {
    acc[field.group] = acc[field.group] || [];
    acc[field.group].push(field);

    return acc;
  }, {});

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={dialogStyle}>
        <div
          style={{
            padding: "18px 22px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 18, color: "#0f172a" }}>
              Amerika Raporu (Container Loading Report)
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
              Adım {stepIndex + 1} / {steps.length} — {activeStep.label}
              {" · "}
              Seçilen fotoğraf: {totalSelected}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{ ...secondaryButtonStyle, padding: "6px 12px" }}
          >
            Kapat
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: 6,
            padding: "12px 22px",
            borderBottom: "1px solid #e2e8f0",
            flexWrap: "wrap",
          }}
        >
          {steps.map((step, index) => {
            const stepCount =
              step.key === "__form"
                ? 0
                : step.key === "__containerView"
                  ? Object.values(containerPhotos).reduce(
                      (sum, urls) => sum + (Array.isArray(urls) ? urls.length : 0),
                      0
                    )
                  : selections[step.key]?.length || 0;

            const done = step.key !== "__form" && stepCount > 0;

            return (
              <button
                key={step.key}
                type="button"
                onClick={() => setStepIndex(index)}
                disabled={busy}
                style={{
                  border: "1px solid",
                  borderColor: index === stepIndex ? "#1677ff" : "#e2e8f0",
                  background:
                    index === stepIndex
                      ? "#e6f0ff"
                      : done
                        ? "#f0fdf4"
                        : "#fff",
                  color: index === stepIndex ? "#1677ff" : "#475569",
                  borderRadius: 999,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                {index + 1}. {step.label}
                {done ? ` (${stepCount})` : ""}
              </button>
            );
          })}
        </div>

        <div style={{ padding: 22, overflowY: "auto", flex: 1 }}>
          {isFormStep ? (
            <FormStep
              values={values}
              onChange={setValue}
              containerRows={containerRows}
              onChangeContainerRow={updateContainerRow}
              bulletGroups={bulletGroups}
            />
          ) : isContainerViewStep ? (
            <ContainerViewStep
              records={records}
              photos={photos}
              containerPhotos={containerPhotos}
              onToggle={toggleContainerPhoto}
              onZoom={setZoomedPhoto}
            />
          ) : (
            <PhotoStep
              key={activeStep.key}
              section={activeStep}
              photos={photos}
              selected={selections[activeStep.key] || []}
              onToggle={handlePhotoToggle}
              onZoom={setZoomedPhoto}
            />
          )}
        </div>

        {(error || progress) && (
          <div
            style={{
              padding: "10px 22px",
              fontSize: 13,
              color: error ? "#b91c1c" : "#1677ff",
              background: error ? "#fef2f2" : "#eff6ff",
            }}
          >
            {error || progress}
          </div>
        )}

        <div
          style={{
            padding: "14px 22px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
            disabled={busy || stepIndex === 0}
            style={{
              ...secondaryButtonStyle,
              opacity: stepIndex === 0 ? 0.5 : 1,
            }}
          >
            Geri
          </button>

          <div style={{ display: "flex", gap: 10 }}>
            {!isLastStep && (
              <button
                type="button"
                onClick={() =>
                  setStepIndex((index) => Math.min(steps.length - 1, index + 1))
                }
                disabled={busy}
                style={secondaryButtonStyle}
              >
                İleri
              </button>
            )}

            <button
              type="button"
              onClick={handleGenerate}
              disabled={busy}
              style={{
                ...primaryButtonStyle,
                background: busy ? "#bfbfbf" : "#1677ff",
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              {busy ? "Oluşturuluyor..." : "Word Raporu Oluştur"}
            </button>
          </div>
        </div>
      </div>

      {zoomedPhoto && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setZoomedPhoto(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.82)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
            padding: 24,
            cursor: "zoom-out",
          }}
        >
          <img
            src={zoomedPhoto.url}
            alt={zoomedPhoto.label || "Fotoğraf"}
            style={{
              maxWidth: "100%",
              maxHeight: "88vh",
              objectFit: "contain",
              borderRadius: 8,
              boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
            }}
          />

          <button
            type="button"
            onClick={() => setZoomedPhoto(null)}
            style={{
              position: "absolute",
              top: 18,
              right: 22,
              border: "none",
              borderRadius: 999,
              width: 38,
              height: 38,
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function FormStep({ values, onChange, containerRows, onChangeContainerRow, bulletGroups }) {
  return (
    <div style={{ display: "grid", gap: 20 }}>
      <section>
        <h4 style={{ margin: "0 0 10px", fontSize: 15, color: "#0f172a" }}>
          A - Particulars of the Loaded Cargoes
        </h4>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 12,
          }}
        >
          {REPORT_TEXT_FIELDS.map((field) => (
            <div
              key={field.key}
              style={field.multiline ? { gridColumn: "1 / -1" } : undefined}
            >
              <label style={labelStyle} htmlFor={`field-${field.key}`}>
                {field.label}
              </label>

              {field.multiline ? (
                <textarea
                  id={`field-${field.key}`}
                  value={values[field.key] || ""}
                  onChange={(event) => onChange(field.key, event.target.value)}
                  rows={2}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              ) : (
                <input
                  id={`field-${field.key}`}
                  value={values[field.key] || ""}
                  onChange={(event) => onChange(field.key, event.target.value)}
                  style={inputStyle}
                />
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h4 style={{ margin: "0 0 10px", fontSize: 15, color: "#0f172a" }}>
          B - Loaded Cargo Quantity As Per Container Number
        </h4>

        <p style={{ margin: "0 0 10px", fontSize: 13, color: "#64748b" }}>
          Seçilen her konteyner için ayrı bir satır — No ve Container No
          otomatik gelir, diğer alanları doldur.
        </p>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr>
                {["No", "Container No", ...REPORT_TABLE_FIELDS.map((f) => f.label)].map(
                  (label) => (
                    <th
                      key={label}
                      style={{
                        textAlign: "left",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#475569",
                        padding: "6px 8px",
                        borderBottom: "1px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>
              {(containerRows || []).map((row, index) => (
                <tr key={row.recordId}>
                  <td style={{ padding: "6px 8px", fontSize: 13, color: "#334155" }}>
                    {index + 1}
                  </td>
                  <td
                    style={{
                      padding: "6px 8px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0f172a",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.containerNo || "-"}
                  </td>

                  {REPORT_TABLE_FIELDS.map((field) => {
                    const isHumidity = field.key === "HUMIDITY";

                    return (
                      <td key={field.key} style={{ padding: "6px 8px" }}>
                        <div
                          style={
                            isHumidity
                              ? {
                                  display: "flex",
                                  alignItems: "center",
                                  minWidth: 150,
                                  border: "1px solid #d9d9d9",
                                  borderRadius: 8,
                                  background: "#fff",
                                  overflow: "hidden",
                                }
                              : undefined
                          }
                        >
                          <input
                            value={row[toRowField(field.key)] || ""}
                            onChange={(event) =>
                              onChangeContainerRow(
                                row.recordId,
                                toRowField(field.key),
                                event.target.value
                              )
                            }
                            style={{
                              ...inputStyle,
                              minWidth: 110,
                              ...(isHumidity
                                ? {
                                    border: "none",
                                    borderRadius: 0,
                                    minWidth: 85,
                                  }
                                : {}),
                            }}
                          />

                          {isHumidity && (
                            <span
                              style={{
                                padding: "0 10px",
                                color: "#475569",
                                fontSize: 13,
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                borderLeft: "1px solid #e2e8f0",
                              }}
                            >
                              g/m3
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h4 style={{ margin: "0 0 10px", fontSize: 15, color: "#0f172a" }}>
          C - Control Result
        </h4>

        <div style={{ display: "grid", gap: 14 }}>
          {Object.entries(bulletGroups).map(([group, fields]) => (
            <div key={group}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#334155",
                  marginBottom: 6,
                }}
              >
                {group}
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                {fields.map((field) => (
                  <textarea
                    key={field.key}
                    value={values[field.key] || ""}
                    onChange={(event) => onChange(field.key, event.target.value)}
                    rows={2}
                    placeholder="Boş bırakılırsa maddede metin görünmez."
                    style={{ ...inputStyle, resize: "vertical" }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// Fotoğrafları ardışık aynı konteynerNo'ya göre gruplar; sıra korunur
// (1. konteynerin fotoğrafları, ardından 2.'nin, ...).
function groupPhotosByContainer(photos) {
  const groups = [];

  photos.forEach((photo) => {
    const last = groups[groups.length - 1];

    if (last && last.containerNo === photo.containerNo) {
      last.photos.push(photo);
    } else {
      groups.push({ containerNo: photo.containerNo, photos: [photo] });
    }
  });

  return groups;
}

function PhotoStep({ section, photos, selected, onToggle, onZoom }) {
  const limit = section.slots.length;
  const groups = groupPhotosByContainer(photos);
  const showContainerHeaders = groups.length > 1;
  // Aynı anda tek konteynerin fotoğrafları render edilsin: hem daha az
  // kalabalık/karışık görünür hem onlarca büyük fotoğrafı aynı anda çözmeye
  // çalışıp tarayıcıyı kasmaz.
  const [expandedIndex, setExpandedIndex] = useState(0);

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <h4 style={{ margin: "0 0 4px", fontSize: 15, color: "#0f172a" }}>
          {section.label} — {section.title}
        </h4>

        <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
          {section.description} Bu bölümde en fazla <strong>{limit}</strong>{" "}
          fotoğraf kullanılır. Seçim sırası rapordaki sırayı belirler. Şu an{" "}
          <strong>
            {selected.length}/{limit}
          </strong>{" "}
          seçili.
        </p>
      </div>

      {photos.length === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: 14 }}>
          Bu kayıtta fotoğraf bulunamadı.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {groups.map((group, groupIndex) => {
            const selectedInGroup = group.photos.filter((photo) =>
              selected.includes(photo.url)
            ).length;
            const isExpanded = !showContainerHeaders || expandedIndex === groupIndex;

            return (
              <div
                key={`${group.containerNo}-${groupIndex}`}
                style={{
                  border: showContainerHeaders ? "1px solid #e2e8f0" : "none",
                  borderRadius: 12,
                  padding: showContainerHeaders ? 12 : 0,
                }}
              >
                {showContainerHeaders && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedIndex(isExpanded ? -1 : groupIndex)
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 8,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                      marginBottom: isExpanded ? 10 : 0,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: "#eef2ff",
                        color: "#3730a3",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {groupIndex + 1}. Konteyner — {group.containerNo}
                      {selectedInGroup > 0 ? ` · ${selectedInGroup} seçili` : ""}
                    </span>

                    <span style={{ color: "#94a3b8", fontSize: 12 }}>
                      {isExpanded ? "Gizle ▲" : "Göster ▼"}
                    </span>
                  </button>
                )}

                {isExpanded && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {group.photos.map((photo) => {
                      const order = selected.indexOf(photo.url);
                      const isSelected = order !== -1;
                      const disabled = !isSelected && selected.length >= limit;

                      return (
                        <PhotoTile
                          key={photo.id || photo.url}
                          photo={photo}
                          isSelected={isSelected}
                          order={order}
                          disabled={disabled}
                          onToggle={onToggle}
                          onZoom={onZoom}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const PhotoTile = memo(function PhotoTile({
  photo,
  isSelected,
  order,
  disabled,
  onToggle,
  recordId,
  onZoom,
}) {
  const onClick = () => onToggle(photo.url, recordId);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(event) => {
        if (!disabled && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick();
        }
      }}
      style={{
        position: "relative",
        padding: 0,
        border: "2px solid",
        borderColor: isSelected ? "#1677ff" : "#e2e8f0",
        borderRadius: 10,
        overflow: "hidden",
        background: "#f8fafc",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <PhotoThumb url={photo.url} alt={photo.label || "Sevkiyat fotoğrafı"} />

      {isSelected && (
        <span
          style={{
            position: "absolute",
            top: 6,
            left: 6,
            background: "#1677ff",
            color: "#fff",
            borderRadius: 999,
            width: 24,
            height: 24,
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {order + 1}
        </span>
      )}

      {onZoom && (
        <button
          type="button"
          title="Büyüt"
          aria-label="Fotoğrafı büyüt"
          onClick={(event) => {
            event.stopPropagation();
            onZoom(photo);
          }}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 26,
            height: 26,
            border: "none",
            borderRadius: 999,
            background: "rgba(15, 23, 42, 0.6)",
            color: "#fff",
            fontSize: 14,
            lineHeight: 1,
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
          }}
        >
          🔍
        </button>
      )}

      <span
        style={{
          display: "block",
          padding: "6px 8px",
          fontSize: 11,
          color: "#475569",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {photo.label || "Fotoğraf"}
      </span>
    </div>
  );
});

// "Konteyner Numarası" adımı: her seçilen konteyner kendi kutusunda, sadece
// kendi fotoğrafları arasından en fazla CONTAINER_VIEW_SLOT_COUNT tane seçilir.
function ContainerViewStep({ records, photos, containerPhotos, onToggle, onZoom }) {
  const list = Array.isArray(records) ? records : [];
  // Aynı anda tek konteynerin galerisi açık kalsın: hem hangi fotoğrafın
  // hangi konteynere ait olduğu netleşir hem onlarca büyük fotoğrafı aynı
  // anda çözmeye çalışıp tarayıcıyı kasmaz.
  const [expandedRecordId, setExpandedRecordId] = useState(
    () => String(list[0]?._id || list[0]?.konteynerNo || "")
  );

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <h4 style={{ margin: "0 0 4px", fontSize: 15, color: "#0f172a" }}>
          Konteyner Numarası — View of Container Numbered
        </h4>

        <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
          Her konteyner için kendi numarasının/dış görünümünün okunduğu en
          fazla <strong>{CONTAINER_VIEW_SLOT_COUNT}</strong> fotoğraf seç.
          Word dosyasında her konteyner kendi başlığıyla ayrı ayrı görünür.
          Aşağıdan bir konteynerin başlığına tıklayarak galerisini
          aç/kapatabilirsin.
        </p>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        {list.map((record, recordIndex) => {
          const recordId = String(record?._id || record?.konteynerNo || "");
          const isExpanded = expandedRecordId === recordId;
          const recordPhotos = isExpanded
            ? photos.filter((photo) => photo.recordId === recordId)
            : [];
          const selected = containerPhotos[recordId] || [];

          return (
            <div
              key={recordId}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 14,
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setExpandedRecordId(isExpanded ? "" : recordId)
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 8,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  marginBottom: isExpanded ? 10 : 0,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: "#eef2ff",
                    color: "#3730a3",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {recordIndex + 1}. Konteyner — {record?.konteynerNo || "-"}
                  {selected.length > 0
                    ? ` · ${selected.length}/${CONTAINER_VIEW_SLOT_COUNT} seçili`
                    : ""}
                </span>

                <span style={{ color: "#94a3b8", fontSize: 12 }}>
                  {isExpanded ? "Gizle ▲" : "Göster ▼"}
                </span>
              </button>

              {isExpanded &&
                (recordPhotos.length === 0 ? (
                  <div style={{ color: "#94a3b8", fontSize: 14 }}>
                    Bu konteynerde fotoğraf bulunamadı.
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {recordPhotos.map((photo) => {
                      const order = selected.indexOf(photo.url);
                      const isSelected = order !== -1;
                      const disabled =
                        !isSelected && selected.length >= CONTAINER_VIEW_SLOT_COUNT;

                      return (
                        <PhotoTile
                          key={photo.id || photo.url}
                          photo={photo}
                          isSelected={isSelected}
                          order={order}
                          disabled={disabled}
                          onToggle={onToggle}
                          recordId={recordId}
                          onZoom={onZoom}
                        />
                      );
                    })}
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
